import { useCallback, useEffect, useState } from "react";
import {
  BookingDetailsDTO,
  getAllBookingsWithDetails,
  OrderDetailsDTO,
} from "./bookingService";
import NewManageBookingDetails from "./NewManageBookingDetails";
import { formatCurrency } from "../../Types/currencyConverter";

interface AllBookingsListProps {
  orders: OrderDetailsDTO[];
  setOrders: React.Dispatch<React.SetStateAction<OrderDetailsDTO[]>>;
  onView: (order: OrderDetailsDTO) => void;
  onEdit: (order: BookingDetailsDTO) => void;
  filter: "all" | "private" | "business" | "dateRange";
}

const AllBookingsList: React.FC<AllBookingsListProps> = ({
  orders,
  setOrders,
  onView,
  onEdit,
  filter,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | "">("");
  const [selectedOrder, setSelectedOrder] = useState<OrderDetailsDTO | null>(
    null
  );

  const [currentPage, setCurrentPage] = useState(1);
  // Default to Created At descending so newest bookings show first
  const [sortColumn, setSortColumn] = useState<string | null>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const itemsPerPage = 10;

  

  const fetchBookings = useCallback(async () => {
    try {
      const response = await getAllBookingsWithDetails();
      console.log("Fetched response:", response);
      if (response.success) {
        if (Array.isArray(response.data)) {
          console.log("Setting orders data:", response.data);
          setOrders(response.data);
          console.log("Orders data set successfully");
        } else {
          console.error("Response data is not an array:", response.data);
        }
        setError("");
      } else {
        setError(response.message);
      }
    } catch (error: any) {
      setError(error.message);
    }
  }, [setOrders]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]); // Run only once when the component mounts

  useEffect(() => {
    console.log("Filtered bookings updated:", filter);
  }, [filter]);

  useEffect(() => {
    console.log("Orders state updated:", orders);
  }, [orders]); // Log orders whenever the state changes

  const handleView = (order: OrderDetailsDTO) => {
    setSelectedOrder(order);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSort = (column: string) => {
    const order = sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortOrder(order);
  };

   // Helper to get the arrival date as a Date object or null
  const getArrivalDate = (order: OrderDetailsDTO): Date | null => {
  if (order.bookings && order.bookings.length > 0 && order.bookings[0].arrivalDate) {
    return new Date(order.bookings[0].arrivalDate);
  }
  return null;
};

// removed unused 'now'

const sortedOrders = [...orders].sort((a, b) => {
  if (sortColumn === "createdAt") {
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (aTime === bTime) return 0;
    return sortOrder === "asc" ? aTime - bTime : bTime - aTime;
  }
  if (sortColumn === "arrivalDate") {
    const aDate = getArrivalDate(a);
    const bDate = getArrivalDate(b);

    // If both dates are missing, treat as equal
    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;

    const now = new Date();
    const currentYear = now.getFullYear();

    // If one is in a past year, push it to the end
    const aYear = aDate.getFullYear();
    const bYear = bDate.getFullYear();

    const aIsPast = aYear < currentYear;
    const bIsPast = bYear < currentYear;

    if (aIsPast && !bIsPast) return 1;
    if (!aIsPast && bIsPast) return -1;

    // Both in current or future year: sort by year ascending (soonest year first)
    if (aYear !== bYear) return aYear - bYear;

    // Same year: sort by month descending (December first)
    const aMonth = aDate.getMonth();
    const bMonth = bDate.getMonth();
    if (aMonth !== bMonth) return bMonth - aMonth;

    // Same month: sort by day descending
    const aDay = aDate.getDate();
    const bDay = bDate.getDate();
    return bDay - aDay;
  }

  // Fallback for other columns
  if (!sortColumn) return 0;
  const aValue = a[sortColumn as keyof OrderDetailsDTO];
  const bValue = b[sortColumn as keyof OrderDetailsDTO];
  if (aValue === undefined || bValue === undefined) return 0;
  if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
  if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
  return 0;
});


  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(orders.length / itemsPerPage);

  const renderSortArrow = (column: string) => {
    if (sortColumn !== column) return null;
    return sortOrder === "asc" ? "▲" : "▼";
  };

  return (
    <>
      <section className="booking-list-ordernumber-section">
        <div className="container-fluid booking-list">
          {selectedOrder ? (
            <NewManageBookingDetails
              order={selectedOrder}
              onEdit={onEdit}
              onClose={() => setSelectedOrder(null)}
              fetchBookings={fetchBookings}
            />
          ) : (
            <>
              <div className="list-header">
                <span onClick={() => handleSort("orderNumber")}>
                  Order Number {renderSortArrow("orderNumber")}
                </span>
                <span onClick={() => handleSort("createdAt")}>
                  Created At {renderSortArrow("createdAt")}
                </span>
                <span onClick={() => handleSort("customerName")}>
                  Customer {renderSortArrow("customerName")}
                </span>
                <span onClick={() => handleSort("arrivalDate")}>
                  Arrival Date {renderSortArrow("arrivalDate")}
                </span>
                {/* <span onClick={() => handleSort("email")}>
            Email {renderSortArrow("email")}
          </span> */}
                <span onClick={() => handleSort("totalPrice")}>
                  Total Price {renderSortArrow("totalPrice")}
                </span>
                <span>Actions</span>
              </div>
              <ul className="list-group">
                {currentOrders.map((order) => (
                  <li key={order.orderNumber} className="list-group-item">
                    <div className="booking-field">
                      <span>{order.orderNumber}</span>
                    </div>
                    <div className="booking-field">
                      <span>
                        {order.createdAt
                          ? new Date(order.createdAt).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                    <div className="booking-field">
                      <span>{order.customerName}</span>
                    </div>
                    <div className="booking-field">
                      <span>
                        <i className="fa-regular fa-calendar"></i>{" "}
                        {order.bookings &&
                        order.bookings.length > 0 &&
                        order.bookings[0].arrivalDate
                          ? new Date(
                              order.bookings[0].arrivalDate
                            ).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                    {/* <div className="booking-field">
                <span><i className="fa-regular fa-envelope"></i> <a href={`mailto:${order.email}`}>{order.email}</a></span>
              </div> */}
                    <div className="booking-field">
                      <span>{formatCurrency(order.totalPrice)}</span>
                    </div>
                    <div className="booking-actions">
                      <button
                        className="btn btn-sm btn-outline-info me-2"
                        onClick={() => handleView(order)}
                      >
                        <i className="fas fa-eye"></i> View
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <div className="pagination mb-5 pb-5">
          <button
            onClick={handlePreviousPage}
            className="page-item"
            disabled={currentPage === 1}
          >
            &laquo; Previous
          </button>
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index + 1}
              onClick={() => handlePageChange(index + 1)}
              className={`page-item ${
                currentPage === index + 1 ? "active" : ""
              }`}
            >
              {index + 1}
            </button>
          ))}
          <button
            onClick={handleNextPage}
            className="page-item"
            disabled={currentPage === totalPages}
          >
            Next &raquo;
          </button>
        </div>
      </section>
    </>
  );
};

export default AllBookingsList;
